import { type ReactNode } from "react";
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
} from "ui";

import { useComboboxPortalContainer } from "src/hooks/useComboboxPortalContainer";

export interface SingleSelectComboboxItem {
  id: string;
  label: string;
}

interface SingleSelectComboboxProps<T extends SingleSelectComboboxItem> {
  items: T[];
  value: string | null;
  onChange: (id: string) => void;
  placeholder: string;
  emptyText: string;
  renderItem?: (item: T) => ReactNode;
}

export function SingleSelectCombobox<T extends SingleSelectComboboxItem>(
  props: SingleSelectComboboxProps<T>,
) {
  const portal = useComboboxPortalContainer();
  const selected =
    props.items.find((item) => {
      return item.id === props.value;
    }) ?? null;

  return (
    <ComboboxRoot
      items={props.items}
      value={selected}
      onValueChange={(item: T | null) => {
        if (item) {
          props.onChange(item.id);
        }
      }}
      itemToStringLabel={(item: T) => item.label}
      isItemEqualToValue={(item: T, value: T) => item.id === value.id}
    >
      <ComboboxInput
        ref={portal.attach}
        className="w-full"
        placeholder={props.placeholder}
      />
      <ComboboxContent container={portal.container}>
        <ComboboxEmpty>{props.emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item: T) => {
            return (
              <ComboboxItem key={item.id} value={item}>
                {props.renderItem ? props.renderItem(item) : item.label}
              </ComboboxItem>
            );
          }}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
}
